/**
 * QuickAddClientModal — coach-side fast-add for new members / non-members.
 *
 * Modal asks for just name + email. On submit:
 *   - Backend creates a roster row with the target status (`lead` or
 *     `non_member`).
 *   - An MNP signup invite is auto-generated + emailed using the MaxSC
 *     branded template.
 *   - The parent clicks the email link → finishes their profile
 *     (waiver, kids, address, payment method on file).
 *
 * The new row appears in the dashboard under whichever tab they were
 * added from. Members start as `lead` and get auto-promoted when they
 * finish their account; non-members stay as `non_member`.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, UserPlus, Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAVOR_COPY = {
    member: {
        title: "Add new member",
        subtitle: "They'll get an email to finish their profile + add a card. Once they're done, their status flips to Member.",
        button: "Send invite",
    },
    non_member: {
        title: "Add new non-member",
        subtitle: "They'll get an email to finish their profile + add a card. They stay as a Non-Member (pack purchases only, no $12/wk subscription).",
        button: "Send invite",
    },
};

export default function QuickAddClientModal({ targetStatus, flavor, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const copy = FLAVOR_COPY[flavor] || FLAVOR_COPY.member;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim() || !email.trim()) {
            toast.error("Name and email both required");
            return;
        }
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/clients/quick-add`,
                {
                    name: name.trim(),
                    email: email.trim().toLowerCase(),
                    phone: phone.trim() || null,
                    status: targetStatus,
                    note: note.trim() || null,
                },
                { withCredentials: true },
            );
            if (r.data?.email_sent) {
                toast.success(`Invite sent to ${email.trim().toLowerCase()}`, {
                    description: "They'll click the link to finish their profile + add a card.",
                });
            } else {
                toast.warning("Profile created but email didn't send", {
                    description: r.data?.email_error || "Use the resend button on the Invites tab to retry.",
                });
            }
            onCreated?.(r.data);
        } catch (err) {
            toast.error("Couldn't add", { description: err?.response?.data?.detail || "Try again." });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="quick-add-modal"
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md"
            >
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Quick add</div>
                        <h3 className="font-display text-2xl tracking-wider mt-0.5">{copy.title}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="quick-add-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-[12px] text-zinc-300 leading-relaxed">{copy.subtitle}</p>

                    <div>
                        <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                            Parent name <span className="text-brand-red">*</span>
                        </label>
                        <div className="relative">
                            <UserPlus className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                data-testid="quick-add-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Jane Smith"
                                autoFocus
                                className="w-full bg-zinc-900 border border-zinc-700 pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red rounded-lg"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                            Email <span className="text-brand-red">*</span>
                        </label>
                        <div className="relative">
                            <Mail className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="email"
                                data-testid="quick-add-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="jane@example.com"
                                className="w-full bg-zinc-900 border border-zinc-700 pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red rounded-lg"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                            Phone <span className="text-zinc-600">(optional)</span>
                        </label>
                        <input
                            type="tel"
                            data-testid="quick-add-phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(425) 555-1234"
                            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red rounded-lg"
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                            Personal note <span className="text-zinc-600">(optional — shows in invite email)</span>
                        </label>
                        <textarea
                            data-testid="quick-add-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value.slice(0, 600))}
                            rows={2}
                            placeholder="e.g. Hey — looking forward to working with Ava and Leo!"
                            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red rounded-lg"
                        />
                    </div>
                </div>

                <div className="px-5 py-4 border-t border-zinc-800 flex justify-end gap-2 bg-zinc-950 rounded-b-2xl">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        data-testid="quick-add-submit"
                        disabled={busy || !name.trim() || !email.trim()}
                        className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 bg-brand-red border border-brand-red text-white hover:bg-brand-red/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {copy.button}
                    </button>
                </div>
            </form>
        </div>
    );
}
